/*
IndexedDB (Dexie) store for the iPad offline cache of read-only AI artifacts
(#2). We persist React Query's own dehydrated state (via the `dehydrate`/
`hydrate` functions exported from @tanstack/react-query) rather than the
@tanstack/query-persist-client packages: those pin an exact @tanstack/query-core
version that mismatches our react-query, shipping two query-core copies so the
persisted client never restores into the app's QueryClient. Using react-query's
own dehydrate/hydrate guarantees a single query-core and zero extra deps.

localStorage can't be used — a single artifact (e.g. a key-individual research
report) can run ~9MB, past the localStorage budget — so it lives in IndexedDB.
Only allow-listed, successful artifact queries are dehydrated (see
artifactQueryPersistence.ts + the dehydrate options in TRPCProvider).

All operations are best-effort: a failed read/write (quota, transient
disconnect) must never break the app — a missing cache just means those
artifacts aren't available offline.
*/

import { type DehydratedState } from "@tanstack/react-query";
import { captureMessage } from "@client/telemetry/sentry";
import Dexie, { type EntityTable } from "dexie";
import { normalizePersistedArtifactState } from "./artifactQueryPersistence";
import { flushLockdownPersistence } from "./lockdownPersistenceControls";
import { recentDurabilityFailures } from "@lib/offline/durabilityFailureLog";

export type PersistedArtifactCache = {
  /** Invalidation key (round-aggregate schema hash); mismatch ⇒ discard. */
  buster: string;
  /**
   * The underlying (logged-in) user the snapshot was written for. AI artifacts
   * are anonymized per viewer, so a snapshot from one account must never hydrate
   * into another — the consumer (TRPCProvider) discards/purges the cache when
   * the live identity doesn't match this. A non-sign-out account change (cookie
   * expiry + relogin as a different user) doesn't run the sign-out clear, so the
   * schemaHash buster alone wouldn't catch it.
   */
  userId: string;
  /** Epoch ms the snapshot was written; used for max-age expiry. */
  timestamp: number;
  state: DehydratedState;
};

type CacheRow = PersistedArtifactCache & { id: string };
type Db = Dexie & { artifactCache: EntityTable<CacheRow, "id"> };

// Exported so the Lockdown persist config (lockdownStandInStorage) can key
// its file mapping on these by construction — a rename here then cannot
// silently detach the cache from its durable file.
export const ARTIFACT_QUERY_CACHE_DB = "ArtifactQueryCache";
export const ARTIFACT_QUERY_CACHE_STORE = "artifactCache";

const ROW_ID = "singleton";

// Max age of a persisted snapshot before it's considered stale. Single source
// of truth: the consumer (TRPCProvider's ArtifactCacheGate) imports this for its
// hydrate-time expiry check, and the merge below uses it so an expired row is
// never resurrected (copied forward with a fresh timestamp).
export const ARTIFACT_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

// Lazily opened on first use (not at import) so test harnesses can wire
// fake-indexeddb before the Dexie instance binds to an IndexedDB backend, and
// so we never open IndexedDB on platforms that never call these helpers.
let _db: Db | null = null;
function db(): Db {
  if (_db == null) {
    _db = new Dexie(ARTIFACT_QUERY_CACHE_DB) as Db;
    _db.version(1).stores({ [ARTIFACT_QUERY_CACHE_STORE]: "id" });
  }
  return _db;
}

// All cache mutations (write + clear) run through this serial chain so they
// can't interleave at the IndexedDB level. Critically, a `clear` issued on an
// impersonation switch must WIN over a persist that was already in flight (or
// scheduled just before): otherwise a write that dehydrated the *previous*
// effective user's artifacts could land after the clear and repopulate the
// singleton with the wrong user's data (query keys omit the viewer — it rides
// on a header). Serializing makes the last-issued op the last to run.
let opChain: Promise<void> = Promise.resolve();
function enqueue(op: () => Promise<void>): Promise<void> {
  const next = opChain.then(op, op);
  opChain = next;
  return next;
}

// Permanently stop persisting. Called on sign-out *before* clearing, so a
// throttled persist (the ArtifactCacheGate's 2s timer, still mounted on the
// sign-out page) can't fire after the clear and repopulate the singleton with
// the previous user's artifacts before the redirect unloads the page.
let writesSuspended = false;
export function suspendArtifactCacheWrites(): void {
  writesSuspended = true;
}

// Await all enqueued cache ops (incl. a write already inside its IndexedDB
// `put`). Sign-out calls this after suspending so a persist that already passed
// the guard can't complete after the DB is deleted and recreate it with the
// previous user's artifacts. Awaiting the current opChain tail is enough: with
// writes suspended, any op enqueued later early-returns at the guard.
export function flushArtifactCacheWrites(): Promise<void> {
  return opChain.then(
    () => {},
    () => {},
  );
}

/**
 * Persist the dehydrated artifact cache (best-effort). MERGES into the existing
 * snapshot (per query hash, new wins) rather than replacing it: a persist only
 * dehydrates queries currently in `success` state, so an allow-listed artifact
 * that's momentarily loading/absent/error must NOT drop the copy a previous
 * snapshot already held. A buster (schema) change replaces wholesale, since the
 * old queries are then stale.
 */
export function writeArtifactCache(
  payload: PersistedArtifactCache,
): Promise<void> {
  return enqueue(async () => {
    if (writesSuspended) return;
    try {
      const existing = await db().artifactCache.get(ROW_ID);
      let state = payload.state;
      // Merge into the existing row only if it's still valid: same schema buster,
      // same user, AND not past its max age. A different user's snapshot must be
      // replaced wholesale (never merged) so one account's artifacts can't bleed
      // into another's. An expired row must NOT be carried forward either —
      // otherwise its stale queries would be rewritten with a fresh timestamp
      // and outlive the expiry window.
      const existingIsFresh =
        existing != null &&
        existing.buster === payload.buster &&
        existing.userId === payload.userId &&
        payload.timestamp - existing.timestamp < ARTIFACT_CACHE_MAX_AGE_MS;
      if (existing != null && existingIsFresh) {
        const byHash = new Map<string, DehydratedState["queries"][number]>();
        for (const q of existing.state.queries) byHash.set(q.queryHash, q);
        for (const q of payload.state.queries) byHash.set(q.queryHash, q);
        // Per-query expiry: the row timestamp is renewed on every persist, so
        // without this an individual query last fetched > MAX_AGE ago would ride
        // along indefinitely as long as any sibling keeps the row fresh (cache
        // A, then refresh B every few weeks ⇒ A never ages out). Drop queries
        // past the window by their OWN dataUpdatedAt. Freshly-dehydrated queries
        // were just fetched, so they always pass.
        const cutoff = payload.timestamp - ARTIFACT_CACHE_MAX_AGE_MS;
        const queries = [...byHash.values()].filter(
          (q) => q.state.dataUpdatedAt >= cutoff,
        );
        state = { ...payload.state, queries };
      }
      await db().artifactCache.put({
        id: ROW_ID,
        buster: payload.buster,
        userId: payload.userId,
        timestamp: payload.timestamp,
        state,
      });
    } catch (error) {
      // Same message as always (grouping stays stable); the WHY rides in
      // extras: Dexie wraps the real failure (`inner`). On the Lockdown
      // stand-in this store is "defer"-mode, so a snapshot-file failure never
      // aborts the transaction — an AbortError here is the browser's own
      // IndexedDB aborting (8N was real IndexedDB on the funder's iPad). The
      // durability ring is still attached for the await-mode stores' sake.
      const inner = (error as { inner?: unknown } | null)?.inner;
      const cause = (error as { cause?: unknown } | null)?.cause;
      captureMessage(`Failed to persist artifact cache: ${String(error)}`, {
        level: "warning",
        extra: {
          inner: inner == null ? undefined : String(inner),
          cause: cause == null ? undefined : String(cause),
          recentDurabilityFailures: recentDurabilityFailures(),
        },
      });
    }
  });
}

/** Read the persisted artifact cache, or null on miss/error (best-effort). */
export async function readArtifactCache(): Promise<PersistedArtifactCache | null> {
  try {
    const row = await db().artifactCache.get(ROW_ID);
    if (row == null) return null;
    return {
      buster: row.buster,
      userId: row.userId,
      timestamp: row.timestamp,
      state: normalizePersistedArtifactState(row.state),
    };
  } catch (error) {
    captureMessage(`Failed to read artifact cache: ${String(error)}`, {
      level: "warning",
      extra: { recentDurabilityFailures: recentDurabilityFailures() },
    });
    return null;
  }
}

/**
 * Discard the persisted artifact cache (e.g. on impersonation change).
 *
 * This is a PRIVACY clear, and on the Lockdown stand-in the row delete commits
 * in memory before the durable file is rewritten ("defer" store) — while the
 * hydrate-time userId check cannot help on an impersonation switch (the row is
 * stamped with the UNDERLYING user, unchanged across a switch). So the clear
 * escalates until the disk state is safe, and only reports success when it is:
 *
 *   1. delete the row, then await the durable file catching up
 *      (`flushLockdownPersistence`; a no-op wherever real IndexedDB makes the
 *      committed delete durable already);
 *   2. if that fails, delete the whole database — strictly stronger, the
 *      stand-in removes the native file (with its own retries), and the cache
 *      is rebuildable so the cost is one refetch;
 *   3. if even that fails, REJECT — a caller must never be told a privacy
 *      clear happened when the previous viewer's data may still be on disk.
 *
 * The escalation steps are injectable for tests (same pattern as
 * `purgeUserDataDatabases`); production uses the real flush and delete.
 */
export function clearArtifactCache(
  deps: {
    flushDurable: () => Promise<void>;
    deleteDatabase: () => Promise<void>;
  } = {
    flushDurable: flushLockdownPersistence,
    deleteDatabase: () => {
      // Release our handle first so the delete cannot deadlock on it, and
      // null it so the next use reopens against the recreated database.
      _db?.close();
      _db = null;
      return Dexie.delete(ARTIFACT_QUERY_CACHE_DB);
    },
  },
): Promise<void> {
  return enqueue(async () => {
    try {
      await db().artifactCache.delete(ROW_ID);
      await deps.flushDurable();
      return;
    } catch (error) {
      captureMessage(
        `Artifact-cache clear could not flush; deleting the database: ${String(error)}`,
        {
          level: "warning",
          extra: { recentDurabilityFailures: recentDurabilityFailures() },
        },
      );
    }
    await deps.deleteDatabase();
  });
}

/** Remove an authoritative denial durably; absent queries otherwise survive merges. */
export async function evictArtifactQuery(queryHash: string): Promise<void> {
  return evictArtifactQueries((query) => query.queryHash === queryHash);
}

/** Remove matching entries, including persisted queries not hydrated in memory. */
export async function evictArtifactQueries(
  matches: (query: DehydratedState["queries"][number]) => boolean,
): Promise<void> {
  try {
    await enqueue(async () => {
      const existing = await db().artifactCache.get(ROW_ID);
      if (!existing) return;
      await db().artifactCache.put({
        ...existing,
        state: {
          ...existing.state,
          queries: existing.state.queries.filter((q) => !matches(q)),
        },
      });
      await flushLockdownPersistence();
    });
  } catch {
    // The existing privacy clear escalates if the native mirror cannot be written.
    await clearArtifactCache();
  }
}
